// bikeData.js - Multi-Tenant Firebase Firestore integration

import { db } from './firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  onSnapshot 
} from 'firebase/firestore';

// Company-specific cache for bikes data
const companyBikesCache = new Map();

// Collection reference
const bikesCollection = collection(db, 'bikes');

// Get company-specific bikes from cache
export const getCompanyBikes = (companyId) => {
  if (!companyId) {
    console.warn('No company ID provided, returning empty array');
    return [];
  }
  return companyBikesCache.get(companyId) || [];
};

// Set company-specific bikes in cache
const setCompanyBikes = (companyId, bikes) => {
  if (!companyId) {
    console.warn('No company ID provided for setting bikes cache');
    return;
  }
  companyBikesCache.set(companyId, bikes);
};

// For backward compatibility - returns bikes for current company
export let motorbikes = [];

// Set current company context for backward compatibility
let currentCompanyId = null;

export const setCurrentCompany = (companyId) => {
  currentCompanyId = companyId;
  motorbikes = getCompanyBikes(companyId);
};

// Initialize bikes from Firestore for a specific company
export const initializeCompanyBikes = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  try {
    console.log(`Loading bikes for company: ${companyId}`);
    
    // Query bikes for specific company
    const q = query(bikesCollection, where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    
    const companyBikes = [];
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      const bikeString = `${bikeData.registrationNumber} - ${bikeData.model}`;
      companyBikes.push(bikeString);
    });
    
    // Update company-specific cache
    setCompanyBikes(companyId, companyBikes);
    
    // Update backward compatibility array if this is the current company
    if (companyId === currentCompanyId) {
      motorbikes.length = 0;
      motorbikes.push(...companyBikes);
    }
    
    console.log(`Bikes loaded for company ${companyId}:`, companyBikes.length);
    return companyBikes;
  } catch (error) {
    console.error(`Error loading bikes for company ${companyId}:`, error);
    throw error;
  }
};

// Initialize bikes from Firestore (backward compatibility)
export const initializeBikes = async (companyId = currentCompanyId) => {
  if (!companyId) {
    console.warn('No company ID provided for initializeBikes');
    return [];
  }
  return await initializeCompanyBikes(companyId);
};

// Real-time listener for company-specific bikes changes
export const subscribeToCompanyBikes = (companyId, callback) => {
  if (!companyId) {
    console.error('Company ID is required for bike subscription');
    return () => {}; // Return empty unsubscribe function
  }

  console.log(`Setting up bike subscription for company: ${companyId}`);
  
  const q = query(bikesCollection, where('companyId', '==', companyId));
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const companyBikes = [];
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      const bikeString = `${bikeData.registrationNumber} - ${bikeData.model}`;
      companyBikes.push(bikeString);
    });
    
    // Update company-specific cache
    setCompanyBikes(companyId, companyBikes);
    
    // Update backward compatibility array if this is the current company
    if (companyId === currentCompanyId) {
      motorbikes.length = 0;
      motorbikes.push(...companyBikes);
    }
    
    console.log(`Bikes updated for company ${companyId}:`, companyBikes.length);
    
    if (callback) callback(companyBikes);
  }, (error) => {
    console.error(`Error in bike subscription for company ${companyId}:`, error);
  });
  
  return unsubscribe;
};

// Real-time listener for bikes changes (backward compatibility)
export const subscribeToBikes = (callback, companyId = currentCompanyId) => {
  if (!companyId) {
    console.warn('No company ID provided for subscribeToBikes');
    return () => {};
  }
  return subscribeToCompanyBikes(companyId, callback);
};

// Get the registration number from the full bike string
export const getRegistrationNumber = (bikeString) => {
  return bikeString.split('-')[0].trim();
};

// Get the model from the full bike string
export const getModel = (bikeString) => {
  return bikeString.split('-')[1].trim();
};

// Group bikes by model for a specific company
export const groupBikesByModel = (companyId = currentCompanyId) => {
  const bikes = companyId ? getCompanyBikes(companyId) : motorbikes;
  const groupedBikes = {};
    
  bikes.forEach(bike => {
    const model = getModel(bike);
      
    if (!groupedBikes[model]) {
      groupedBikes[model] = [];
    }
      
    groupedBikes[model].push(bike);
  });
    
  return groupedBikes;
};

// Get unique bike models for a specific company
export const getBikeModels = (companyId = currentCompanyId) => {
  const bikes = companyId ? getCompanyBikes(companyId) : motorbikes;
  return Array.from(new Set(bikes.map(bike => getModel(bike))));
};

// Count bikes by model for a specific company
export const getModelCounts = (companyId = currentCompanyId) => {
  const bikes = companyId ? getCompanyBikes(companyId) : motorbikes;
  const models = {};
    
  bikes.forEach(bike => {
    const model = getModel(bike);
    models[model] = (models[model] || 0) + 1;
  });
    
  return models;
};

// Filter bikes by model for a specific company
export const filterBikesByModel = (model, companyId = currentCompanyId) => {
  const bikes = companyId ? getCompanyBikes(companyId) : motorbikes;
  return bikes.filter(bike => getModel(bike) === model);
};

// Check if a bike is available (not currently rented) for a specific company
export const isBikeAvailable = (bikeString, bookings = [], companyId = currentCompanyId) => {
  // Filter bookings by company if companyId is provided
  const companyBookings = companyId 
    ? bookings.filter(booking => booking.companyId === companyId)
    : bookings;
    
  return !companyBookings.some(booking => booking.bike === bikeString && booking.isActive);
};

// Calculate bike availability statistics for a specific company
export const getAvailabilityStats = (bookings = [], companyId = currentCompanyId) => {
  const bikes = companyId ? getCompanyBikes(companyId) : motorbikes;
  const totalBikes = bikes.length;
  
  // Filter bookings by company if companyId is provided
  const companyBookings = companyId 
    ? bookings.filter(booking => booking.companyId === companyId)
    : bookings;
    
  const rentedBikes = companyBookings.filter(booking => booking.isActive).length;
  const availableBikes = totalBikes - rentedBikes;
    
  return {
    totalBikes,
    rentedBikes,
    availableBikes,
    availabilityPercentage: totalBikes > 0 ? Math.round((availableBikes / totalBikes) * 100) : 0
  };
};

// MULTI-TENANT FIREBASE FIRESTORE OPERATIONS

// Add a new bike to Firestore for a specific company
export const addBike = async (regNumber, model, companyId, userContext = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  try {
    const bikeString = `${regNumber} - ${model}`;
    
    // Check if bike already exists in company cache
    const companyBikes = getCompanyBikes(companyId);
    if (companyBikes.includes(bikeString)) {
      throw new Error('Bike already exists in your company');
    }
    
    // Check if bike exists for this company in Firestore
    const existingBikeQuery = query(
      bikesCollection,
      where('companyId', '==', companyId),
      where('registrationNumber', '==', regNumber),
      where('model', '==', model)
    );
    
    const existingBikes = await getDocs(existingBikeQuery);
    if (!existingBikes.empty) {
      throw new Error('Bike already exists in your company');
    }
    
    // Add to Firestore with company context
    const docRef = await addDoc(bikesCollection, {
      registrationNumber: regNumber,
      model: model,
      companyId: companyId,
      createdAt: new Date(),
      createdBy: userContext.userId || 'unknown',
      createdByUsername: userContext.username || 'unknown',
      isActive: true,
      // Additional company context
      companyName: userContext.companyName || 'Unknown Company'
    });
    
    // Update company-specific cache
    const updatedCompanyBikes = [...companyBikes, bikeString];
    setCompanyBikes(companyId, updatedCompanyBikes);
    
    // Update backward compatibility array if this is the current company
    if (companyId === currentCompanyId) {
      motorbikes.push(bikeString);
    }
    
    console.log(`Bike added for company ${companyId} with ID:`, docRef.id);
    return bikeString;
  } catch (error) {
    console.error(`Error adding bike for company ${companyId}:`, error);
    throw error;
  }
};

// Update an existing bike in Firestore for a specific company
export const updateBike = async (oldBikeString, newRegNumber, newModel, companyId, userContext = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  try {
    const newBikeString = `${newRegNumber} - ${newModel}`;
    
    const companyBikes = getCompanyBikes(companyId);
    const index = companyBikes.indexOf(oldBikeString);
    if (index === -1) {
      throw new Error('Bike not found in your company');
    }
    
    // Find the document in Firestore for this company
    const oldRegNumber = getRegistrationNumber(oldBikeString);
    const oldModel = getModel(oldBikeString);
    
    const q = query(
      bikesCollection,
      where('companyId', '==', companyId),
      where('registrationNumber', '==', oldRegNumber),
      where('model', '==', oldModel)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Bike not found in your company records');
    }
    
    // Check if new bike details already exist for this company
    if (oldBikeString !== newBikeString) {
      const existingBikeQuery = query(
        bikesCollection,
        where('companyId', '==', companyId),
        where('registrationNumber', '==', newRegNumber),
        where('model', '==', newModel)
      );
      
      const existingBikes = await getDocs(existingBikeQuery);
      if (!existingBikes.empty) {
        throw new Error('A bike with these details already exists in your company');
      }
    }
    
    // Update the first matching document
    const docToUpdate = querySnapshot.docs[0];
    await updateDoc(docToUpdate.ref, {
      registrationNumber: newRegNumber,
      model: newModel,
      updatedAt: new Date(),
      updatedBy: userContext.userId || 'unknown',
      updatedByUsername: userContext.username || 'unknown'
    });
    
    // Update company-specific cache
    const updatedCompanyBikes = [...companyBikes];
    updatedCompanyBikes[index] = newBikeString;
    setCompanyBikes(companyId, updatedCompanyBikes);
    
    // Update backward compatibility array if this is the current company
    if (companyId === currentCompanyId) {
      const motorbikesIndex = motorbikes.indexOf(oldBikeString);
      if (motorbikesIndex !== -1) {
        motorbikes[motorbikesIndex] = newBikeString;
      }
    }
    
    console.log(`Bike updated for company ${companyId}`);
    return newBikeString;
  } catch (error) {
    console.error(`Error updating bike for company ${companyId}:`, error);
    throw error;
  }
};

// Delete a bike from Firestore for a specific company
export const deleteBike = async (bikeString, companyId, userContext = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  try {
    const companyBikes = getCompanyBikes(companyId);
    const index = companyBikes.indexOf(bikeString);
    
    if (index === -1) {
      throw new Error('Bike not found in your company');
    }
    
    // Find the document in Firestore for this company
    const regNumber = getRegistrationNumber(bikeString);
    const model = getModel(bikeString);
    
    const q = query(
      bikesCollection,
      where('companyId', '==', companyId),
      where('registrationNumber', '==', regNumber),
      where('model', '==', model)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('Bike not found in your company records');
    }
    
    // Delete the first matching document
    const docToDelete = querySnapshot.docs[0];
    await deleteDoc(docToDelete.ref);
    
    // Update company-specific cache
    const updatedCompanyBikes = companyBikes.filter((_, i) => i !== index);
    setCompanyBikes(companyId, updatedCompanyBikes);
    
    // Update backward compatibility array if this is the current company
    if (companyId === currentCompanyId) {
      const motorbikesIndex = motorbikes.indexOf(bikeString);
      if (motorbikesIndex !== -1) {
        motorbikes.splice(motorbikesIndex, 1);
      }
    }
    
    console.log(`Bike deleted for company ${companyId}`);
    return true;
  } catch (error) {
    console.error(`Error deleting bike for company ${companyId}:`, error);
    throw error;
  }
};

// Get all companies that have bikes (for super admin purposes)
export const getAllCompaniesWithBikes = async () => {
  try {
    const querySnapshot = await getDocs(bikesCollection);
    const companies = new Set();
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      if (bikeData.companyId) {
        companies.add(bikeData.companyId);
      }
    });
    
    return Array.from(companies);
  } catch (error) {
    console.error('Error getting companies with bikes:', error);
    throw error;
  }
};

// Get bike count by company (for analytics)
export const getBikeCountByCompany = async () => {
  try {
    const querySnapshot = await getDocs(bikesCollection);
    const companyCounts = {};
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      const companyId = bikeData.companyId || 'unknown';
      companyCounts[companyId] = (companyCounts[companyId] || 0) + 1;
    });
    
    return companyCounts;
  } catch (error) {
    console.error('Error getting bike counts by company:', error);
    throw error;
  }
};

// Seed initial data to Firestore for a specific company
export const seedCompanyBikes = async (companyId, userContext = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required for seeding bikes');
  }

  try {
    const initialBikes = [
      "BHV 9643 - DIO", "BGP 5744 - DIO", 
      "BFI 6187 - DIO", "BFS 5971 - DIO", "BGX 6738 - DIO",
      "BFW 4365 - DIO", "BHN 2365 - APRILIA", "BIY 1275 - APRILIA",
      "BJA 4111 - APRILIA", "BIL 4884 - APRILIA", "BJA 9609 - APRILIA",
      "BIZ 1720 - APRILIA", "BJB 9696 - APRILIA", "BIP 2394 - APRILIA",
      "BJB 4337 - APRILIA", "BIX 1635 - XPULSE", "BIT 3854 - XPULSE",
      "BJA 5538 - XPULSE", "BJB 5390 - XPULSE", "BJA 8081 - XPULSE",
      "BIM 3391 - XBLADE", "BIM 3367 - XBLADE", "BIH 0779 - HORNET",
      "BHE 2987 - HORNET", "BJB 1585 - HORNET", "BAS 3859 - SHINE",
      "BHA 3087 - NAVI", "BAX 9057 - TRIGGER", "BIG 3474 - NTORQ",
      "BIZ 5467 - HONDA CD", "BER 3067 - XR", "BIY 1944 - HORNET",
      "BHI 8585 - HORNET"
    ];
    
    console.log(`Starting to seed initial bikes for company: ${companyId}`);
    
    let addedCount = 0;
    let skippedCount = 0;
    
    for (const bikeString of initialBikes) {
      const regNumber = getRegistrationNumber(bikeString);
      const model = getModel(bikeString);
      
      // Check if bike already exists for this company
      const q = query(
        bikesCollection,
        where('companyId', '==', companyId),
        where('registrationNumber', '==', regNumber),
        where('model', '==', model)
      );
      
      const existingBikes = await getDocs(q);
      
      if (existingBikes.empty) {
        await addDoc(bikesCollection, {
          registrationNumber: regNumber,
          model: model,
          companyId: companyId,
          createdAt: new Date(),
          createdBy: userContext.userId || 'seeder',
          createdByUsername: userContext.username || 'System Seeder',
          isActive: true,
          companyName: userContext.companyName || 'Unknown Company'
        });
        console.log(`Added: ${bikeString} for company ${companyId}`);
        addedCount++;
      } else {
        console.log(`Skipped (already exists): ${bikeString} for company ${companyId}`);
        skippedCount++;
      }
    }
    
    console.log(`Seeding completed for company ${companyId}! Added: ${addedCount}, Skipped: ${skippedCount}`);
    
    // Refresh company cache
    await initializeCompanyBikes(companyId);
    
    return { addedCount, skippedCount };
  } catch (error) {
    console.error(`Error seeding bikes for company ${companyId}:`, error);
    throw error;
  }
};

// Seed initial data to Firestore (backward compatibility)
export const seedInitialBikes = async (companyId = currentCompanyId, userContext = {}) => {
  if (!companyId) {
    throw new Error('Company ID is required for seeding bikes');
  }
  return await seedCompanyBikes(companyId, userContext);
};

// Company management functions
export const getCompanyBikeStats = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  try {
    const q = query(bikesCollection, where('companyId', '==', companyId));
    const querySnapshot = await getDocs(q);
    
    const stats = {
      totalBikes: 0,
      bikesByModel: {},
      activeBikes: 0,
      inactiveBikes: 0
    };
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      stats.totalBikes++;
      
      // Count by model
      const model = bikeData.model;
      stats.bikesByModel[model] = (stats.bikesByModel[model] || 0) + 1;
      
      // Count active/inactive
      if (bikeData.isActive !== false) {
        stats.activeBikes++;
      } else {
        stats.inactiveBikes++;
      }
    });
    
    return stats;
  } catch (error) {
    console.error(`Error getting bike stats for company ${companyId}:`, error);
    throw error;
  }
};

// Clear company cache (useful for logout)
export const clearCompanyCache = (companyId) => {
  if (companyId) {
    companyBikesCache.delete(companyId);
  }
  
  // Clear current company context
  if (companyId === currentCompanyId) {
    currentCompanyId = null;
    motorbikes.length = 0;
  }
};

// Clear all caches (useful for complete logout)
export const clearAllCaches = () => {
  companyBikesCache.clear();
  currentCompanyId = null;
  motorbikes.length = 0;
};

// Initialize for multiple companies (useful for super admin)
export const initializeMultipleCompanies = async (companyIds) => {
  const results = {};
  
  for (const companyId of companyIds) {
    try {
      results[companyId] = await initializeCompanyBikes(companyId);
    } catch (error) {
      console.error(`Failed to initialize bikes for company ${companyId}:`, error);
      results[companyId] = [];
    }
  }
  
  return results;
};

// Export current company context getter for debugging
export const getCurrentCompanyContext = () => ({
  currentCompanyId,
  cacheSize: companyBikesCache.size,
  cachedCompanies: Array.from(companyBikesCache.keys()),
  currentCompanyBikeCount: motorbikes.length
});